import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Field, FieldArray, useFormikContext } from 'formik';
import { connect } from 'react-redux';
import axios from 'axios';
import get from 'lodash/get';
import { PorletHeaderTitle } from 'components/porletHeader';
import { mainNeedDinoc, substrateTypeOPTs } from 'enums/formOtps';
import { getDistributorsOptions } from 'redux/distributors/selector';
import { getCountryProfile } from 'redux/profile/selector';
import { requests as distributorsRequest } from 'redux/distributors/reducer';
import { getDinocSheetPreviewSrc } from 'preview/sheetImageSrc';
import TextField from 'components/form/input';
import Autocomplete from 'components/form/autocomplete';
import '../../newDinoc/form/step3/previewer.scss';

import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/Delete';

const API_URL = window.env.API_URL;
const ACCESS_TOKEN = window.env.ACCESS_TOKEN;

const newSheet = (type, data = {}) => ({
  type,
  substrate: '',
  need: '',
  family: '',
  meters: '',
  run: '',
  film: null,
  custom: '',
  distributor: data?.distributor ?? {},
});

export const initialValues = {
  data: {
    ...newSheet(''),
  },
};

function normalizeDinocFilmsPayload(data) {
  const list = Array.isArray(data) ? data : data?.data;

  return list;
}

async function fetchDinocProductsByFamily(family) {
  const url = `${API_URL}/films/arqfinishes?family=${family}`;
  try {
    const response = await axios.get(url, {
      params: { family },
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`,
      },
    });
    const raw = normalizeDinocFilmsPayload(response.data);
    const items = Array.isArray(raw) ? raw : [];
    return items
      .map((row) => ({
        ...row,
        label: row.label ?? row.name,
      }))
      .sort((a, b) =>
        String(a.label ?? '').localeCompare(String(b.label ?? ''), undefined, {
          sensitivity: 'base',
        }),
      );
  } catch (err) {
    console.error('fetchDinocProductsByFamily', err);
    return [];
  }
}

function normalizeFamiliesPayload(data) {
  const list = Array.isArray(data) ? data : data?.data;
  return Array.isArray(list) ? list : [];
}

async function fetchFamiliesByProgram(program, type) {
  const url = `${API_URL}/families/${program}?type=${type}`;
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem(ACCESS_TOKEN)}`,
      },
    });
    const items = normalizeFamiliesPayload(response.data);
    return items.map((value) => ({
      label: value.name,
      value: value.name,
      type: value.type,
    }));
  } catch (err) {
    console.error('fetchFamiliesByProgram', err);
    return [];
  }
}

function filmOptionsForRow(family, loadedProducts, savedFilm, filmData) {
  if (!family) {
    return [];
  }
  if (family === 'Custom') {
    return [{ value: 'Custom', label: 'Custom' }];
  }

  const filmStr =
    savedFilm != null && savedFilm !== '' ? String(savedFilm).trim() : '';
  const savedLabel = filmData?.name != null ? String(filmData.name) : filmStr;

  const fromApi = Array.isArray(loadedProducts)
    ? loadedProducts.map((item) => ({
        label: item.label ?? item.name,
        value: item.label ?? item.name,
      }))
    : [];

  if (filmStr && !fromApi.some((o) => String(o.value) === filmStr)) {
    return [{ value: filmStr, label: savedLabel || filmStr }, ...fromApi];
  }
  return fromApi;
}

const dinocEditSheetsMapStateToProps = (state) => ({
  distributorsOptions: getDistributorsOptions(state),
});

const dinocEditSheetsMapDispatchToProps = (dispatch) => ({
  fetchDistributors: () => dispatch(distributorsRequest.fetch()),
});

const DinocEditApplicationRow = ({
  index,
  values,
  familiesOptions,
  filteredOptions,
  installerCountry,
  onRemove,
  showRemoveButton,
}) => {
  const { setFieldValue } = useFormikContext();
  const family = values.data.applications[index]?.family;
  const prevFamilyRef = useRef(family);
  const [familyProducts, setFamilyProducts] = useState(undefined);

  useEffect(() => {
    const prev = prevFamilyRef.current;
    if (prev != null && family != null && prev !== family) {
      setFieldValue(`data.applications[${index}].film`, null);
      setFieldValue(`data.applications[${index}].custom`, '');
    }
    prevFamilyRef.current = family;
  }, [family, index, setFieldValue]);

  useEffect(() => {
    let cancelled = false;

    if (!family) {
      setFamilyProducts(undefined);
      return undefined;
    }
    if (family === 'Custom') {
      setFamilyProducts(undefined);
      return undefined;
    }

    setFamilyProducts(null);
    fetchDinocProductsByFamily(family).then((items) => {
      if (!cancelled) {
        setFamilyProducts(items);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [family]);

  const filmDisabled =
    !family || (family !== 'Custom' && familyProducts == null);

  const sheet = values.data.applications[index];
  const previewSrc = getDinocSheetPreviewSrc({
    family: sheet?.family,
    film: sheet?.film,
    familyProducts,
    filmData: sheet?.filmData,
  });

  return (
    <>
      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="Sustrato: *" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <Field
            name={`data.applications[${index}].substrate`}
            component={Autocomplete}
            options={substrateTypeOPTs}
            type="text"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>
      </div>
      <div className="row FormRow">
        <div className="col-sm-12 col-md-12 col-lg-4">
          <FormattedMessage id="Necesidad Principal: *" />
        </div>
        <div className="col-sm-12 col-md-12 col-lg-8">
          <Field
            name={`data.applications[${index}].need`}
            options={mainNeedDinoc}
            component={Autocomplete}
            type="text"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </div>
      </div>

      <div key={index}>
        <div className="row FormRow">
          <div className="col-sm-12 col-md-12 col-lg-4">
            <FormattedMessage id="Categoría" />
          </div>
          <div className="col-sm-12 col-md-12 col-lg-8">
            <Field
              name={`data.applications[${index}].family`}
              component={Autocomplete}
              variant="outlined"
              translate={false}
              options={[
                ...familiesOptions,
                { value: 'Custom', label: 'Custom' },
              ]}
            />
          </div>
        </div>

        <div className="row FormRow">
          <div className="col-sm-12 col-md-12 col-lg-4">
            <FormattedMessage id="Producto" />
          </div>
          <div className="col-sm-12 col-md-12 col-lg-8">
            <Field
              name={`data.applications[${index}].film`}
              component={Autocomplete}
              variant="outlined"
              translate={false}
              disabled={filmDisabled}
              options={filmOptionsForRow(
                family,
                familyProducts,
                sheet?.film,
                sheet?.filmData,
              )}
              textFieldProps={{
                onBlur: () => {
                  const row = values.data.applications[index];
                  const keepCustom =
                    row?.film == 'Custom' && row?.family == 'Custom';
                  if (!keepCustom) {
                    setFieldValue(`data.applications[${index}].custom`, '');
                  }
                },
              }}
            />
          </div>
        </div>
        <div className="row FormRow">
          <div className="col-sm-12 col-md-12 col-lg-4">
            <FormattedMessage id="M2 Necesarios: *" />
          </div>
          <div className="col-sm-12 col-md-12 col-lg-8">
            <Field
              name={`data.applications[${index}].meters`}
              component={TextField}
              type="text"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </div>

        {sheet.custom === '' ? (
          <></>
        ) : (
          <div className="row FormRow">
            <div className="col-sm-12 col-md-12 col-lg-4">
              <FormattedMessage id="Descripción" />
            </div>
            <div className="col-sm-12 col-md-12 col-lg-8">
              <Field
                name={`data.applications[${index}].custom`}
                component={TextField}
                type="text"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>
        )}
        <div className="row FormRow">
          <div className="col-sm-12 col-md-12 col-lg-4">
            <FormattedMessage id="Vista Previa" />
          </div>
          <div className="col-sm-12 col-md-12 col-lg-8">
            <img
              src={previewSrc}
              className="img-previewer-summary w-100 "
              alt=""
            />
          </div>
        </div>

        {installerCountry !== 'AR' ? (
          <div className="row FormRow">
            <div className="col-sm-12 col-md-12 col-lg-4">
              <FormattedMessage id="Distribuidor: *" />
            </div>
            <div className="col-sm-12 col-md-12 col-lg-8">
              <Field
                name={`data.applications[${index}].distributor.id`}
                component={Autocomplete}
                variant="outlined"
                options={filteredOptions}
                placeholder="Seleccionar"
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>
        ) : (
          <div className="row FormRow">
            <div className="col-sm-12 col-md-12 col-lg-4">
              <FormattedMessage id="Distribuidor: *" />
            </div>
            <div className="col-sm-12 col-md-12 col-lg-8">
              <div className="kt-space-10" />
              <span>{sheet.distributor?.name}</span>
            </div>
          </div>
        )}
        <div className="row FormRow">
          <div className="col-sm-12 col-md-12 col-lg-4">
            <FormattedMessage id="Número de RUN: *" />
          </div>
          <div className="col-sm-12 col-md-12 col-lg-8">
            <Field
              name={`data.applications[${index}].run`}
              component={TextField}
              type="text"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </div>

        {showRemoveButton && (
          <div className="row">
            <div className="col-12 kt-space-20" />
            <div className="col-12">
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={onRemove}
              >
                <DeleteIcon color="primary" />
                <FormattedMessage id="Eliminar esta lámina" />
              </Button>
              <div className="col-12 kt-space-12" />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const DinocEditApplicationsInner = ({
  values,
  country,
  distributorsOptions,
  fetchDistributors,
}) => {
  const [familiesOptions, setFamiliesOptions] = useState([]);

  useEffect(() => {
    fetchDistributors();
  }, [fetchDistributors]);

  useEffect(() => {
    let cancelled = false;
    fetchFamiliesByProgram('arqfinishes', 'dinoc').then((options) => {
      if (!cancelled) {
        setFamiliesOptions(options);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredOptions = distributorsOptions;

  // const distributorFiltered = distributorsOptions.filter((distributor) => {
  //   return distributor.users.some((user) => user.dinoc === true);
  // });

  // const filteredOptions = distributorFiltered.filter((option) => {
  //   const isNLorBE = country === 'NL' || country === 'BE';

  //   if (isNLorBE == true) {
  //     return (
  //       !option.label.toLowerCase().includes('3m') &&
  //       !option.label.toLowerCase().includes('test') &&
  //       !option.label.toLowerCase().includes('distributor') &&
  //       !/^[a-z]+distributor/.test(option.label.toLowerCase())
  //     );
  //   } else {
  //     return (
  //       !option.label.toLowerCase().includes('test') &&
  //       !option.label.toLowerCase().includes('distributor') &&
  //       !/^[a-z]+distributor/.test(option.label.toLowerCase())
  //     );
  //   }
  // });

  const applications = get(values, 'data.applications', []) || [];
  const theDistributor = applications?.[0]?.distributor;
  const installerCountry = values.installer?.country;

  return (
    <FieldArray name="data.applications">
      {({ push, remove }) => (
        <>
          {applications.map((_, index) => (
            <DinocEditApplicationRow
              key={index}
              index={index}
              values={values}
              familiesOptions={familiesOptions}
              filteredOptions={filteredOptions}
              installerCountry={installerCountry}
              onRemove={() => remove(index)}
              showRemoveButton={applications.length > 1}
            />
          ))}
          {applications.length < 4 && (
            <>
              <div className="kt-space-20" />
              <div className="row">
                <div className="col-12">
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() =>
                      push(newSheet(2, { distributor: theDistributor }))
                    }
                  >
                    <FormattedMessage id="Agregar nueva lámina" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </FieldArray>
  );
};

const DinocEditApplications = connect(
  dinocEditSheetsMapStateToProps,
  dinocEditSheetsMapDispatchToProps,
)(DinocEditApplicationsInner);

export const Sheets = ({ values, country }) => {
  return (
    <>
      <PorletHeaderTitle variant="h5">
        <FormattedMessage id="3M™ DI-NOC™" />
      </PorletHeaderTitle>
      <DinocEditApplications values={values} country={country} />
      <div className="kt-space-20" />

      <div className="kt-space-20" />
    </>
  );
};

export const mapStateToProps = (state) => ({
  country: getCountryProfile(state),
});

export default connect(mapStateToProps)(Sheets);
